import { useEffect, useRef } from 'react';
import { useErrorHandler } from '../utils/errorHandler';

export function useDropdownFormInitializer<T>(
  visible: boolean,
  loadData: () => Promise<Record<string, any>>,
  setStateFunctions: Record<string, (value: any) => void>,
  reset: (values: T) => void,
  initialValues: T | (() => T),
  /** After a successful loadData + reset (for example to mount embedded grids only then) */
  onInitialized?: () => void
) {
  const { handleError } = useErrorHandler();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    const loadAndReset = async () => {
      // Only load data once when dialog becomes visible
      if (!visible || hasLoadedRef.current) return;

      // Get current initial values (in case it's a function)
      const currentInitialValues = typeof initialValues === 'function'
        ? (initialValues as () => T)()
        : initialValues;

      try {
        hasLoadedRef.current = true;
        const data = await loadData();

        // Set dropdown state in caller
        Object.entries(data).forEach(([key, value]) => {
          if (typeof value !== 'undefined' && setStateFunctions[key]) {
            setStateFunctions[key](value);
          }
        });

        // Reset form with initial values after data is loaded
        reset(currentInitialValues);
        onInitialized?.();
      } catch (err) {
        handleError(err, 'loading form data');
        // IMPORTANT: even when the dropdown data could not be loaded, initialize the form
        // anyway and call onInitialized. Otherwise the embedded grids stay stuck in the
        // 'loading...' state forever (nestedGridsReady never becomes true) - to the user
        // that looks like an empty dialog. This way the fields stay usable
        // (possibly with empty dropdowns); reopening starts a fresh load attempt.
        reset(currentInitialValues);
        onInitialized?.();
      }
    };

    loadAndReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally runs only when visible changes; other deps (loadData, initialValues, reset, etc.) are from caller and would cause infinite loops
  }, [visible]);

  // Reset the flag when dialog closes
  useEffect(() => {
    if (!visible) {
      hasLoadedRef.current = false;
    }
  }, [visible]);
} 